import { stringifyReplacer } from "./misc";

export type EnumIdentifier               = string;
export type Enum                         = { identifier: EnumIdentifier };
export type EnumSpec<TEnum extends Enum> = { [key: EnumIdentifier]: TEnum };
export type NamedEnum                    = Enum & { name: string };

export function enums<TSpec extends EnumSpec<TEnum>, TEnum extends Enum>(spec: TSpec) {
  if (spec === undefined               ) throw new Error("Parameter spec must be defined.");
  if (spec === null                    ) throw new Error("Parameter spec must be defined.");
  if (Object.entries(spec).length === 0) throw new Error("Parameter spec must not be empty.");

  const entries = Object.entries(spec).map(([identifier, _]) => [identifier, { ..._,
    __skipDeepCopy: true,
    identifier,
    toJSON(): any {
      return _.identifier;
    }
  }]);

  return Object.fromEntries(entries) as TSpec;
}
export function items<TEnum extends Enum>(spec: EnumSpec<TEnum>) {
  if (spec === undefined               ) throw new Error("Parameter spec must be defined.");
  if (spec === null                    ) throw new Error("Parameter spec must be defined.");
  if (Object.entries(spec).length === 0) throw new Error("Parameter spec must not be empty.");

  const mismatchedItems = Object.entries(spec).filter(
    ([identifier, _]) => _.identifier !== identifier
  );
  if (mismatchedItems.length > 0) throw new Error(
    `Identifier mismatch found in enum spec. ${JSON.stringify(mismatchedItems, stringifyReplacer, 2)}`
  );

  return Object.values(spec);
}
export function enumByIdentifier<TEnum extends Enum>(enums: TEnum[]) {
  if (enums === undefined) throw new Error("Parameter enums must be defined.");
  if (enums === null     ) throw new Error("Parameter enums must be defined.");
  if (enums.length === 0 ) throw new Error("Parameter enums must not be empty.");

  return (identifier: EnumIdentifier) => {
    if (identifier === undefined) throw new Error("Parameter identifier must be defined.");
    if (identifier === null     ) throw new Error("Parameter identifier must be defined.");

    const result = enums.find(_ => _.identifier === identifier);
    if (result === undefined) throw new Error(`Enum with identifier "${identifier}" not found in ${JSON.stringify(enums, stringifyReplacer, 2)}`);

    return result;
  }
}

export function enumWith<TEnum extends Enum>(enums: TEnum[]) {
  if (enums === undefined) throw new Error("Parameter enums must be defined.");
  if (enums === null     ) throw new Error("Parameter enums must be defined.");
  if (enums.length === 0 ) throw new Error("Parameter enums must not be empty.");

  return (partial: Partial<TEnum>) => {
    if (partial === undefined) throw new Error("Parameter partial must be defined.");
    if (partial === null     ) throw new Error("Parameter partial must be defined.");
    if (Object.keys(partial).length === 0) throw new Error("Parameter partial must not be empty.");

    const result = enums.find(e => {
      for (const key in partial) {
        if (partial[key as keyof TEnum] !== e[key as keyof TEnum]) return false;
      }
      return true;
    });
    if (result === undefined) throw new Error(`Expected exactly one enum matching ${JSON.stringify(partial, stringifyReplacer, 2)}, but found none in ${JSON.stringify(enums, stringifyReplacer, 2)}`);

    return result;
  }
}

export function tryEnumWith<TEnum extends Enum>(enums: TEnum[]) {
  if (enums === undefined) throw new Error("Parameter enums must be defined.");
  if (enums === null     ) throw new Error("Parameter enums must be defined.");
  if (enums.length === 0 ) throw new Error("Parameter enums must not be empty.");

  return (partial: Partial<TEnum>) => {
    if (partial === undefined) throw new Error("Parameter partial must be defined.");
    if (partial === null     ) throw new Error("Parameter partial must be defined.");
    if (Object.keys(partial).length === 0) throw new Error("Parameter partial must not be empty.");
    const result = enums.find(e => {
      for (const key in partial) {
        if (partial[key as keyof TEnum] !== e[key as keyof TEnum]) return false;
      }
      return true;
    });
    return result;
  };
}

export function enumsWith<TEnum extends Enum>(enums: TEnum[]) {
  if (enums === undefined) throw new Error("Parameter enums must be defined.");
  if (enums === null     ) throw new Error("Parameter enums must be defined.");
  if (enums.length === 0 ) throw new Error("Parameter enums must not be empty.");

  return (partial: Partial<TEnum>) => {
    if (partial === undefined) throw new Error("Parameter partial must be defined.");
    if (partial === null     ) throw new Error("Parameter partial must be defined.");
    if (Object.keys(partial).length === 0) throw new Error("Parameter partial must not be empty.");

    const results = enums.filter(e => {
      for (const key in partial) {
        if (partial[key as keyof TEnum] !== e[key as keyof TEnum]) return false;
      }
      return true;
    })
    return results;
  }
}

export function areEqualEnums<TEnum extends Enum>(a?: TEnum | EnumIdentifier, b?: TEnum | EnumIdentifier) {
  if (a !== undefined && !isEnum(a) && !isEnumIdentifier(a)) throw new Error("Parameter a must be undefined or an enum or an enum identifier, if defined. It is: " + JSON.stringify(a, stringifyReplacer, 2));
  if (b !== undefined && !isEnum(b) && !isEnumIdentifier(b)) throw new Error("Parameter b must be undefined or an enum or an enum identifier, if defined. It is: " + JSON.stringify(b, stringifyReplacer, 2));

  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;

  const aIdentifier = isEnumIdentifier(a) ? a : a.identifier;
  const bIdentifier = isEnumIdentifier(b) ? b : b.identifier;

  return aIdentifier === bIdentifier;
}
export const equalsToEnum = <TEnum extends Enum>(e: TEnum | EnumIdentifier) => {
  if (e === undefined) throw new Error("Parameter e must be defined.");
  if (e === null     ) throw new Error("Parameter e must be defined.");
  if (!isEnum(e) && !isEnumIdentifier(e)) throw new Error("Parameter e must be an enum or an enum identifier. It is: " + JSON.stringify(e, stringifyReplacer, 2));

  return (_?: TEnum | EnumIdentifier) => {
    if (_ !== undefined && !isEnum(_) && !isEnumIdentifier(_)) throw new Error("Parameter _ must be undefined or an enum or an enum identifier, if defined. It is: " + JSON.stringify(_, stringifyReplacer, 2));

    return areEqualEnums(e, _);
  }
}

function isEnumIdentifier(_: any): _ is EnumIdentifier {
  return typeof _ === "string" && _ !== "";
}
function isEnum(_: any): _ is Enum {
  return typeof _ === "object" && "identifier" in _;
}
